export type FormattedLogLine = { readonly message: string; readonly rest: string | null };

type LogRecord = { readonly msg: string } & Record<string, unknown>;

// LandIQ's logger writes one JSON object per line; `ts` repeats the time the line already shows.
const SHOWN_KEYS: ReadonlySet<string> = new Set(['msg', 'ts']);

function isLogRecord(value: unknown): value is LogRecord {
  return typeof value === 'object' && value !== null && typeof (value as { msg?: unknown }).msg === 'string';
}

function parseRecord(text: string): LogRecord | null {
  if (!text.trimStart().startsWith('{')) return null;

  try {
    const value: unknown = JSON.parse(text);

    return isLogRecord(value) ? value : null;
  } catch {
    return null;
  }
}

const formatValue = (value: unknown) => (typeof value === 'string' ? value : JSON.stringify(value));

/** A JSON log record leads with its `msg`, the other fields follow as `key=value`; any other line stays as it is. */
export function formatLogLine(text: string): FormattedLogLine {
  const record = parseRecord(text);

  if (record === null) return { message: text, rest: null };

  const rest = Object.entries(record)
    .filter(([key]) => !SHOWN_KEYS.has(key))
    .map(([key, value]) => `${key}=${formatValue(value)}`)
    .join(' ');

  return { message: record.msg, rest: rest === '' ? null : rest };
}
